import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Heart, 
  MessageCircle, 
  Share2, 
  Eye,
  Calendar,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { type SocialAnalytics, SocialProfile, Platform } from '@/types/social'
import { subDays, format } from 'date-fns'

// Mock analytics data
const mockAnalytics: SocialAnalytics[] = [
  {
    profile_id: '1',
    date_range: { start: '2025-01-01', end: '2025-01-14' },
    metrics: {
      follower_growth: 127,
      total_posts: 18,
      avg_engagement_rate: 6.8,
      total_reach: 42500,
      total_impressions: 68300,
      top_performing_posts: ['post1', 'post2', 'post3'],
      best_posting_times: ['09:00', '15:00', '19:00'],
      hashtag_performance: [
        { hashtag: '#MasonryRepair', usage_count: 12, avg_engagement: 450, trending_score: 8.5 },
        { hashtag: '#TorontoConstruction', usage_count: 8, avg_engagement: 380, trending_score: 7.2 },
        { hashtag: '#BrickRepair', usage_count: 10, avg_engagement: 520, trending_score: 9.1 }
      ]
    },
    platform_specific: {
      instagram: {
        stories_views: 12800,
        reels_plays: 25600,
        profile_visits: 890
      }
    }
  },
  {
    profile_id: '2',
    date_range: { start: '2025-01-01', end: '2025-01-14' },
    metrics: {
      follower_growth: 89,
      total_posts: 15,
      avg_engagement_rate: 5.2,
      total_reach: 28400,
      total_impressions: 45200,
      top_performing_posts: ['post4', 'post5'],
      best_posting_times: ['08:00', '12:00', '17:00'],
      hashtag_performance: [
        { hashtag: '#HamiltonMasonry', usage_count: 10, avg_engagement: 320, trending_score: 6.8 },
        { hashtag: '#BrickWork', usage_count: 7, avg_engagement: 280, trending_score: 6.1 },
        { hashtag: '#StoneRepair', usage_count: 5, avg_engagement: 410, trending_score: 7.5 }
      ]
    },
    platform_specific: {
      facebook: {
        page_likes: 45,
        post_reach: 28400,
        video_views: 8900
      }
    }
  }
]

const mockProfiles: SocialProfile[] = [
  {
    id: '1',
    name: 'FixMyBrick Toronto',
    platform: 'INSTAGRAM',
    franchise_region: 'toronto',
    account_id: 'fixmybrick_toronto',
    username: '@fixmybrick_toronto',
    display_name: 'FixMyBrick Toronto',
    profile_image_url: '',
    follower_count: 2627,
    credentials: null,
    settings: null,
    is_active: true,
    connection_status: 'connected',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    name: 'FixMyBrick Hamilton',
    platform: 'FACEBOOK',
    franchise_region: 'hamilton',
    account_id: 'fixmybrick_hamilton',
    username: 'FixMyBrick Hamilton',
    display_name: 'FixMyBrick Hamilton',
    profile_image_url: '',
    follower_count: 1889,
    credentials: null,
    settings: null,
    is_active: true,
    connection_status: 'connected',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z'
  }
]

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

export default function SocialAnalytics() {
  const [selectedProfile, setSelectedProfile] = useState<string>('all')
  const [dateRange, setDateRange] = useState<string>('14d')
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Mock data queries
  const { data: analytics = mockAnalytics } = useQuery({
    queryKey: ['analytics', selectedProfile, dateRange],
    queryFn: () => Promise.resolve(mockAnalytics)
  })

  const { data: profiles = mockProfiles } = useQuery({
    queryKey: ['profiles'],
    queryFn: () => Promise.resolve(mockProfiles)
  })

  const getAggregatedMetrics = () => {
    if (selectedProfile !== 'all') {
      return analytics.find(a => a.profile_id === selectedProfile)?.metrics || analytics[0]?.metrics
    }

    // Aggregate metrics across all profiles
    const aggregated = analytics.reduce((acc, curr) => {
      return {
        follower_growth: acc.follower_growth + curr.metrics.follower_growth,
        total_posts: acc.total_posts + curr.metrics.total_posts,
        avg_engagement_rate: acc.avg_engagement_rate + curr.metrics.avg_engagement_rate,
        total_reach: acc.total_reach + curr.metrics.total_reach,
        total_impressions: acc.total_impressions + curr.metrics.total_impressions,
        top_performing_posts: [...acc.top_performing_posts, ...curr.metrics.top_performing_posts],
        best_posting_times: [...new Set([...acc.best_posting_times, ...curr.metrics.best_posting_times])],
        hashtag_performance: [...acc.hashtag_performance, ...curr.metrics.hashtag_performance]
      }
    }, {
      follower_growth: 0,
      total_posts: 0,
      avg_engagement_rate: 0,
      total_reach: 0,
      total_impressions: 0,
      top_performing_posts: [] as string[],
      best_posting_times: [] as string[],
      hashtag_performance: [] as any[]
    })

    // Calculate averages where needed
    if (analytics.length > 0) {
      aggregated.avg_engagement_rate = aggregated.avg_engagement_rate / analytics.length
    }

    return aggregated
  }

  const metrics = getAggregatedMetrics()

  const getGrowthPercentage = (value: number, baseline: number = 1000) => {
    // Mock calculation - in real app, this would compare with previous period
    const growth = ((value - baseline) / baseline) * 100
    return growth.toFixed(1)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    setIsRefreshing(false)
  }

  const handleExport = () => {
    // Implement export functionality
    console.log('Exporting analytics data...')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Social Analytics</h1>
          <p className="text-muted-foreground">
            Track performance across all your social media platforms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Analytics Overview</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Profiles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Profiles</SelectItem>
                  {profiles.map(profile => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.display_name || profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="14d">Last 14 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Follower Growth</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">+{metrics?.follower_growth || 0}</div>
            <div className="flex items-center text-xs text-green-600">
              <TrendingUp className="h-3 w-3 mr-1" />
              +{getGrowthPercentage(metrics?.follower_growth || 0)}% from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(metrics?.total_reach || 0).toLocaleString()}</div>
            <div className="flex items-center text-xs text-green-600">
              <TrendingUp className="h-3 w-3 mr-1" />
              +{getGrowthPercentage(metrics?.total_reach || 0, 35000)}% from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement Rate</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(metrics?.avg_engagement_rate || 0).toFixed(1)}%</div>
            <div className="flex items-center text-xs text-green-600">
              <TrendingUp className="h-3 w-3 mr-1" />
              +{getGrowthPercentage(metrics?.avg_engagement_rate || 0, 5.5)}% from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.total_posts || 0}</div>
            <div className="flex items-center text-xs text-muted-foreground">
              <Calendar className="h-3 w-3 mr-1" />
              {((metrics?.total_posts || 0) / 14).toFixed(1)} posts/day avg
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="content">Content Performance</TabsTrigger>
          <TabsTrigger value="audience">Audience Insights</TabsTrigger>
          <TabsTrigger value="optimization">Optimization</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Platform Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Platform Performance</CardTitle>
                <CardDescription>
                  Performance breakdown by social media platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {profiles.map(profile => {
                    const profileAnalytics = analytics.find(a => a.profile_id === profile.id)
                    if (!profileAnalytics) return null

                    return (
                      <div key={profile.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[profile.platform]}`} />
                          <div>
                            <p className="font-medium">{profile.display_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {profile.follower_count?.toLocaleString()} followers
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {profileAnalytics.metrics.avg_engagement_rate.toFixed(1)}% engagement
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {profileAnalytics.metrics.total_reach.toLocaleString()} reach
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Engagement Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>Engagement Breakdown</CardTitle>
                <CardDescription>
                  How your audience interacts with your content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      <span className="text-sm">Likes</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">2,847</p>
                      <p className="text-xs text-green-600">+12.3%</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">Comments</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">384</p>
                      <p className="text-xs text-green-600">+8.7%</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Share2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Shares</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">156</p>
                      <p className="text-xs text-green-600">+15.2%</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-purple-500" />
                      <span className="text-sm">Profile Visits</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">892</p>
                      <p className="text-xs text-green-600">+22.1%</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Hashtags */}
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Hashtags</CardTitle>
              <CardDescription>
                Hashtags that drive the most engagement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {(metrics?.hashtag_performance || []).slice(0, 6).map((hashtag: any, index: number) => (
                  <div key={hashtag.hashtag} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-blue-600">{hashtag.hashtag}</span>
                      <Badge variant="secondary">#{index + 1}</Badge>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Usage:</span>
                        <span>{hashtag.usage_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Engagement:</span>
                        <span>{hashtag.avg_engagement}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Trending Score:</span>
                        <span className="font-medium">{hashtag.trending_score}/10</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Content Performance Analysis</CardTitle>
              <CardDescription>
                Detailed insights into your content performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Content Analytics</h3>
                <p className="text-muted-foreground">
                  Detailed content performance charts and insights would be displayed here.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audience" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Best Posting Times</CardTitle>
                <CardDescription>
                  Optimal times to reach your audience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(metrics?.best_posting_times || []).map((time: string, index: number) => (
                    <div key={time} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <span className="font-medium">{time}</span>
                      <Badge variant="outline">
                        Peak Time #{index + 1}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Audience Demographics</CardTitle>
                <CardDescription>
                  Who's engaging with your content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Audience demographic data would be displayed here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="optimization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Optimization Recommendations</CardTitle>
              <CardDescription>
                AI-powered suggestions to improve your social media performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-green-500 bg-green-50">
                  <h4 className="font-medium text-green-800">Content Timing</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Your engagement rate is 23% higher when posting between 9-11 AM. Consider scheduling more content during this window.
                  </p>
                </div>
                <div className="p-4 border-l-4 border-blue-500 bg-blue-50">
                  <h4 className="font-medium text-blue-800">Hashtag Strategy</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Posts with 8-12 hashtags perform 34% better. Your current average is 6 hashtags per post.
                  </p>
                </div>
                <div className="p-4 border-l-4 border-purple-500 bg-purple-50">
                  <h4 className="font-medium text-purple-800">Visual Content</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Before/after carousel posts generate 2.3x more engagement than single images. Create more transformation content.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}